// src/views/BatchMaster/AddEditBatch.jsx (FULL)

import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardTitle,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormText,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../../constants/ApiService';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import useVendorBranch from '../../utils/useVendorBranch';
import Select from 'react-select';
import TimeSelector from '../../components/TimeSelector';
import conditionalDataLoad from '../../utils/conditionalDataLoad';
import {
  addDuration,
  getToday,
  formatDate,
} from '../../constants/DateFormatter';

const makeTempId = () =>
  (crypto?.randomUUID && crypto.randomUUID()) ||
  Math.random().toString(36).slice(2);

const dayOptions = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' },
];

const getDayLabel = (dayId) =>
  dayOptions.find((x) => Number(x.value) === Number(dayId))?.label || '';

const buildDefaultTimeTableState = () => ({
  id: 0,
  class_id: 0,
  batch_id: 0,
  day_id: '',
  start_time: '',
  end_time: '',
  subject_id: 0,
  faculty_id: '',
  vendor_id: 0,
  branch_id: 0,
  class_name: '',
  batch_name: '',
  subject_name: '',
  subject_code: '',
  faculty_name: '',
  day_name: '',
});

const normalizeTimeSelectorValue = (value) => {
  if (value == null) return '';

  const raw = String(value).trim();
  if (!raw || raw === '--') return '';

  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(raw)) return raw;

  const twelveHour = raw.match(
    /^(\d{1,2})\s*:\s*(\d{2})(?:\s*:\s*(\d{2}))?\s*(AM|PM)$/i,
  );
  if (twelveHour) {
    const [, hh, mm, ss = '00', ap] = twelveHour;
    return `${String(hh).padStart(2, '0')}:${mm}:${ss} ${ap.toUpperCase()}`;
  }

  return raw;
};

const toApiTime = (value) => {
  const raw = normalizeTimeSelectorValue(value);
  if (!raw) return null;

  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(raw)) {
    const [hh, mm, ss = '00'] = raw.split(':');
    const hours = Number(hh);
    const minutes = Number(mm);
    const seconds = Number(ss);

    if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds))
      return null;

    if (
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59 ||
      seconds < 0 ||
      seconds > 59
    )
      return null;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
      2,
      '0',
    )}:${String(seconds).padStart(2, '0')}`;
  }

  const match = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!match) return null;

  let [, hh, mm, ss = '00', ampm] = match;
  let hours = Number(hh);
  const minutes = Number(mm);
  const seconds = Number(ss);

  if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds))
    return null;

  if (
    hours < 1 ||
    hours > 12 ||
    minutes < 0 ||
    minutes > 59 ||
    seconds < 0 ||
    seconds > 59
  )
    return null;

  ampm = ampm.toUpperCase();
  if (ampm === 'AM' && hours === 12) hours = 0;
  if (ampm === 'PM' && hours < 12) hours += 12;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0',
  )}:${String(seconds).padStart(2, '0')}`;
};

const timeToSeconds = (value) => {
  const raw = toApiTime(value);
  if (!raw) return null;
  const [hh, mm, ss] = raw.split(':').map(Number);
  return hh * 3600 + mm * 60 + ss;
};

const AddEditBatch = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const { user } = useSelector((state) => state.authState);

  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [rawClasses, setRawClasses] = useState([]);
  const [totSems, setTotSems] = useState(0);
  const [courseFormat, setCourseFormat] = useState('SEM');

  const [semesterSubjects, setSemesterSubjects] = useState({});
  const [subjectLookup, setSubjectLookup] = useState({});

  const facultyMapRef = useRef({ byCs: {}, bySemSub: {} });

  const touchedRef = useRef({
    batch_name: false,
    batch_code: false,
    batch_start_date: false,
    batch_end_date: false,
  });

  const courseAutoReqRef = useRef(0);

  const [batchMasterData, setBatchMasterData] = useState({
    id: 0,
    batch_name: '',
    batch_code: '',
    batch_start_time: '',
    batch_end_time: '',
    batch_start_date: getToday(),
    batch_end_date: '',
    faculty_id: '',
    vendor_id: 0,
    branch_id: 0,
    class_name: '',
    class_id: 0,
    course_structure: '',
    duration: '',
    total_semester: 0,
    session_year: '',
  });

  const [batchErrors, setBatchErrors] = useState({});
  const [faculty, setFaculty] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sessionOptions, setSessionOptions] = useState([]);

  const didInitialAutoLoad = useRef(false);

  const [showTimeTableModal, setShowTimeTableModal] = useState(false);
  const [timeTableLoading, setTimeTableLoading] = useState(false);
  const [timeTableErrors, setTimeTableErrors] = useState({});
  const [timeTableData, setTimeTableData] = useState(
    buildDefaultTimeTableState(),
  );
  const [timeTableContext, setTimeTableContext] = useState({
    semester: 0,
    index: -1,
  });
  const [subjectTimeTableRows, setSubjectTimeTableRows] = useState([]);
  const [subjectTimeTableLoading, setSubjectTimeTableLoading] = useState(false);
  const [timeTableDeletingId, setTimeTableDeletingId] = useState(0);

  const resetTouchedAutoFields = () => {
    touchedRef.current = {
      batch_name: false,
      batch_code: false,
      batch_start_date: false,
      batch_end_date: false,
    };
  };

  const buildDefaultBatchCode = (cls, opt) => {
    const raw =
      (cls &&
        (cls.class_code ||
          cls.class_display_name ||
          cls.class_Name ||
          cls.className)) ||
      opt?.label ||
      '';
    const upper = raw.toString().trim().toUpperCase();
    const cleaned = upper.replace(/[^A-Z0-9]/g, '');
    return cleaned.substring(0, 3);
  };

  const getBatchCode = async (vendorId, branchId, classId) => {
    try {
      const response = await apiService.get(
        `api/BatchMaster/NewBatchCode?vendorId=${vendorId}&branchId=${branchId}&classId=${classId}`,
      );
      if (response?.status === true) return response?.message || '';
    } catch (err) {
      console.error(err);
      return '';
    }
    return '';
  };

  const autoFillBatchFieldsByCourse = async (cls, opt, force = false) => {
    if (!cls || !opt?.value) return;

    const vendorId = batchMasterData.vendor_id;
    const branchId = batchMasterData.branch_id;
    const classId = opt.value;

    const startDate = getToday();
    const endDate = cls?.duration ? addDuration(startDate, cls.duration) : '';

    const courseName = cls?.class_Name || opt?.label || '';
    const defaultName = courseName ? `${courseName} - Batch` : 'Batch';
    const defaultCode = buildDefaultBatchCode(cls, opt);

    setBatchMasterData((prev) => {
      const courseChanged = Number(prev.class_id) !== Number(classId);
      const next = { ...prev };
      const canOverwrite = force || courseChanged;

      if (canOverwrite || (!touchedRef.current.batch_name && !prev.batch_name))
        next.batch_name = defaultName;

      if (
        canOverwrite ||
        (!touchedRef.current.batch_start_date && !prev.batch_start_date)
      )
        next.batch_start_date = startDate;

      if (
        canOverwrite ||
        (!touchedRef.current.batch_end_date && !prev.batch_end_date)
      )
        next.batch_end_date = endDate;

      if (canOverwrite || (!touchedRef.current.batch_code && !prev.batch_code))
        next.batch_code = defaultCode;

      return next;
    });

    const reqId = ++courseAutoReqRef.current;
    const apiCode = await getBatchCode(vendorId, branchId, classId);
    if (!apiCode) return;
    if (courseAutoReqRef.current !== reqId) return;

    setBatchMasterData((prev) => {
      if (Number(prev.class_id) !== Number(classId)) return prev;
      const canOverwrite = force || !touchedRef.current.batch_code;
      if (canOverwrite) return { ...prev, batch_code: apiCode };
      return prev;
    });
  };

  const applyFacultyMapToSemesters = (sems, byCs, bySemSub) => {
    if (!sems || Object.keys(sems).length === 0) return sems;

    let changed = false;
    const next = {};

    Object.keys(sems).forEach((semKey) => {
      const semNum = Number(semKey) || 0;
      const rows = sems[semKey] || [];

      next[semKey] = rows.map((r) => {
        const csId = Number(r.class_subject_trx_id || 0);
        const sid = Number(r.subject_id || 0);

        const pick =
          (csId && byCs?.[csId]) ||
          (semNum && sid && bySemSub?.[`${semNum}|${sid}`]) ||
          null;

        if (!pick) return r;

        const fid = String(pick.fid || '');
        const fname = pick.fname || '';

        const sameId = String(r.faculty_id || '') === fid;
        const sameName = (r.faculty_name || '') === fname;

        if (sameId && sameName) return r;

        changed = true;
        return { ...r, faculty_id: fid, faculty_name: fname };
      });
    });

    return changed ? next : sems;
  };

  const getApiEnvelope = (response) =>
    typeof response?.status === 'boolean'
      ? response
      : typeof response?.data?.status === 'boolean'
        ? response.data
        : typeof response?.data?.value?.status === 'boolean'
          ? response.data.value
          : typeof response?.value?.status === 'boolean'
            ? response.value
            : response;

  const formatTimeForDisplay = (value) => {
    if (!value) return '--';

    const raw = String(value).trim();
    const match = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);

    if (!match) return raw;

    let hours = Number(match[1]);
    const minutes = match[2];
    const ampm = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12 || 12;

    return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
  };

  const loadSubjectTimeTables = async ({
    classId,
    batchId,
    subjectId,
    vendorId,
    branchId,
  }) => {
    if (!classId || !batchId || !subjectId) {
      setSubjectTimeTableRows([]);
      return;
    }

    setSubjectTimeTableLoading(true);

    try {
      const params = new URLSearchParams();

      if (vendorId) params.append('vendorId', vendorId);
      if (branchId) params.append('branchId', branchId);
      if (classId) params.append('classId', classId);
      if (batchId) params.append('batchId', batchId);
      if (subjectId) params.append('subjectId', subjectId);

      const response = await apiService.get(
        `api/TimeTableMaster/GetAllTimeTable?${params.toString()}`,
      );

      const envelope = getApiEnvelope(response);

      const rawList =
        (Array.isArray(envelope?.Data) && envelope.Data) ||
        (Array.isArray(envelope?.data) && envelope.data) ||
        [];

      const filteredList = rawList
        .filter(
          (x) => Number(x.subject_id ?? x.subjectId ?? 0) === Number(subjectId),
        )
        .sort((a, b) => {
          const dayDiff =
            Number(a.day_id ?? a.dayId ?? 0) - Number(b.day_id ?? b.dayId ?? 0);

          if (dayDiff !== 0) return dayDiff;

          return String(a.start_time ?? a.startTime ?? '').localeCompare(
            String(b.start_time ?? b.startTime ?? ''),
          );
        });

      setSubjectTimeTableRows(filteredList);
    } catch (err) {
      console.error(err);
      setSubjectTimeTableRows([]);
      toast.error('Failed to load subject time table');
    } finally {
      setSubjectTimeTableLoading(false);
    }
  };

  const onHandleBack = () => navigate(-1);

  const onBatchInputChange = (e) => {
    const { name, value } = e.target;

    if (
      name === 'batch_name' ||
      name === 'batch_code' ||
      name === 'batch_start_date' ||
      name === 'batch_end_date'
    ) {
      touchedRef.current[name] = true;
    }

    if (name === 'batch_start_date') {
      const cls = (rawClasses || []).find(
        (x) => Number(x.id) === Number(batchMasterData.class_id),
      );
      const endDate = cls?.duration ? addDuration(value, cls.duration) : '';

      setBatchMasterData((prev) => ({
        ...prev,
        [name]: value,
        batch_end_date: endDate,
      }));
    } else {
      setBatchMasterData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    setBatchErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
  };

  const onSelectChange = (name, opt) => {
    const val = opt ? String(opt.value) : '';

    setBatchMasterData((prev) => ({
      ...prev,
      [name]: val,
    }));
    setBatchErrors((prev) => ({ ...prev, [name]: '' }));

    if (name === 'faculty_id') {
      setSemesterSubjects((prev) => {
        const next = {};
        let changed = false;

        Object.keys(prev || {}).forEach((semKey) => {
          next[semKey] = (prev[semKey] || []).map((r) => {
            if (String(r.faculty_id || '').trim()) return r;
            changed = true;
            const f = faculty.find((x) => String(x.value) === val);
            return { ...r, faculty_id: val, faculty_name: f?.label || '' };
          });
        });

        return changed ? next : prev;
      });
    }
  };

  const loadSubjectLookup = async (vendorId, branchId) => {
    try {
      const params = new URLSearchParams();
      params.append('vendorId', vendorId);
      params.append('branchId', branchId);
      params.append('search', '');

      const url = `api/SubjectMaster/GetSubjectMaster?${params.toString()}`;
      const res = await apiService.get(url);

      const container = res?.data ?? res;

      const list =
        (Array.isArray(container?.data) && container.data) ||
        (Array.isArray(container?.Data) && container.Data) ||
        (Array.isArray(container?.list) && container.list) ||
        (Array.isArray(container?.result) && container.result) ||
        (Array.isArray(container) && container) ||
        [];

      const map = {};
      list.forEach((s) => {
        const id = s.id ?? s.subject_id ?? s.SubjectId ?? s.ID ?? s.subjectId;
        const code =
          s.subject_code ??
          s.Subject_Code ??
          s.code ??
          s.SubjectCode ??
          s.subjectCode ??
          '';
        const name =
          s.subject_name ??
          s.Subject_Name ??
          s.name ??
          s.Name ??
          s.SubjectName ??
          s.subjectName ??
          '';
        if (id) map[String(id)] = { code, name };
      });

      setSubjectLookup(map);
    } catch (err) {
      console.error(err);
      setSubjectLookup({});
    }
  };

  const mergeLookupIntoSemesterSubjects = () => {
    setSemesterSubjects((prev) => {
      const updated = {};
      let changed = false;

      Object.keys(prev || {}).forEach((semKey) => {
        const rows = prev[semKey] || [];
        updated[semKey] = rows.map((r) => {
          const sid = r.subject_id ? String(r.subject_id) : '';
          if (!sid) return r;

          const lk = subjectLookup[sid];
          if (!lk) return r;

          const next = { ...r };
          if (!next.subject_code && lk.code) {
            next.subject_code = lk.code;
            changed = true;
          }
          if (!next.subject_name && lk.name) {
            next.subject_name = lk.name;
            changed = true;
          }
          return next;
        });
      });

      return changed ? updated : prev;
    });
  };

  useEffect(() => {
    if (subjectLookup && Object.keys(subjectLookup).length > 0) {
      mergeLookupIntoSemesterSubjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectLookup]);

  const fetchSessions = async (searchText = '') => {
    try {
      const vendorId = batchMasterData.vendor_id || '';
      const branchId = batchMasterData.branch_id || '';

      const res = await apiService.get(
        `api/SessionMaster/GetAllSession?vendorId=${vendorId}&branchId=${branchId}&search=${encodeURIComponent(
          searchText || '',
        )}`,
      );

      const container = res?.data ?? res;

      const list =
        (Array.isArray(container?.data) && container.data) ||
        (Array.isArray(container?.Data) && container.Data) ||
        (Array.isArray(container) && container) ||
        [];

      const opts = (list || [])
        .map((x) => {
          const id = x.id ?? x.ID ?? 0;
          const name =
            x.session_name ?? x.sessionName ?? x.session_year ?? x.sessionYear;
          const label = String(name || '').trim();
          return id && label ? { value: String(id), label } : null;
        })
        .filter(Boolean);

      setSessionOptions(opts);

      setBatchMasterData((prev) => {
        const cur = String(prev.session_year || '').trim();
        if (!cur) return prev;

        const byValue = opts.find((o) => String(o.value) === cur);
        if (byValue) return { ...prev, session_year: String(byValue.label) };

        return prev;
      });
    } catch (err) {
      console.error(err);
      setSessionOptions([]);
    }
  };

  const prefillSubjectsFromClass = (totalSem, list) => {
    const grouped = {};
    (list || []).forEach((s) => {
      const sem = Number(s.semester ?? s.Semester ?? s.semester_no ?? 1) || 1;
      if (!grouped[sem]) grouped[sem] = [];
      grouped[sem].push(s);
    });

    const newSemesters = {};
    const ts = Number(totalSem) || 0;

    for (let i = 1; i <= ts; i++) {
      const semList = grouped[i] || [];

      newSemesters[i] =
        semList.length > 0
          ? semList.map((s) => {
              const sid =
                s.subject_id ??
                s.SubjectId ??
                s.Subject_ID ??
                s.subjectId ??
                s.SubjectID ??
                '';

              const apiName =
                s.subject_name ??
                s.Subject_Name ??
                s.name ??
                s.Name ??
                s.subjectName ??
                '';

              const apiCode =
                s.subject_code ??
                s.Subject_Code ??
                s.code ??
                s.subjectCode ??
                '';

              const lk = sid ? subjectLookup[String(sid)] : null;

              return {
                tempId: makeTempId(),
                id: 0,
                class_subject_trx_id: Number(
                  s.id ?? s.class_subject_trx_id ?? s.classSubjectTrxId ?? 0,
                ),
                subject_id: sid,
                subject_name: apiName || (lk?.name ?? ''),
                subject_code: apiCode || (lk?.code ?? ''),
                faculty_id: '',
                faculty_name: '',
                semester: i,
                isExisting: true,
              };
            })
          : [];
    }

    const map = facultyMapRef.current || { byCs: {}, bySemSub: {} };
    const merged = applyFacultyMapToSemesters(
      newSemesters,
      map.byCs,
      map.bySemSub,
    );

    setSemesterSubjects(merged);
  };

  const loadClassSubjects = async (vendorId, branchId, classId, totalSem) => {
    try {
      if (!classId) {
        setSemesterSubjects({});
        return;
      }

      const params = new URLSearchParams();
      if (vendorId) params.append('vendorId', vendorId);
      if (branchId) params.append('branchId', branchId);
      params.append('classId', classId);

      const url = `api/ClassMaster/GetClassSubjects?${params.toString()}`;
      const res = await apiService.get(url);
      const container = res?.data ?? res;

      const list =
        (Array.isArray(container?.ClassSubjectList) &&
          container.ClassSubjectList) ||
        (Array.isArray(container?.classSubjectList) &&
          container.classSubjectList) ||
        (Array.isArray(container?.data) && container.data) ||
        (Array.isArray(container?.Data) && container.Data) ||
        (Array.isArray(container) && container) ||
        [];

      prefillSubjectsFromClass(totalSem, list);
    } catch (err) {
      console.error(err);
      toast.error('Error loading class subjects');
      setSemesterSubjects({});
    }
  };

  const loadFacultys = async (batch_id) => {
    try {
      if (!batch_id) return;

      const params = new URLSearchParams();
      params.append('id', batch_id);

      const url = `api/BatchMaster/GetBatchSubTrxById?${params.toString()}`;
      const res = await apiService.get(url);

      const container = res?.batchSubjectsList ?? res?.data ?? res;

      const list =
        (Array.isArray(container?.batchSubjectsList) &&
          container.batchSubjectsList) ||
        (Array.isArray(container?.BatchSubjectsList) &&
          container.BatchSubjectsList) ||
        (Array.isArray(container?.data) && container.data) ||
        (Array.isArray(container?.Data) && container.Data) ||
        (Array.isArray(container) && container) ||
        [];

      if (!list.length) return;

      const mapByClassSubTrxId = {};
      const mapBySemSubject = {};

      list.forEach((x) => {
        const csId = Number(
          x.class_subject_trx_id ??
            x.classSubjectTrxId ??
            x.class_subjectTrxId ??
            0,
        );
        const sem = Number(x.semester ?? x.Semester ?? 0);
        const sid = Number(x.subject_id ?? x.subjectId ?? 0);

        const fid = String(x.faculty_id ?? '');
        const fname = x.faculty_name ?? '';

        if (csId) mapByClassSubTrxId[csId] = { fid, fname };
        if (sem && sid) mapBySemSubject[`${sem}|${sid}`] = { fid, fname };
      });

      facultyMapRef.current = {
        byCs: mapByClassSubTrxId,
        bySemSub: mapBySemSubject,
      };

      setSemesterSubjects((prev) =>
        applyFacultyMapToSemesters(prev, mapByClassSubTrxId, mapBySemSubject),
      );
    } catch (err) {
      console.error(err);
      toast.error('Error loading batch faculties');
    }
  };

  useEffect(() => {
    if (!location.state) return;

    const st = { ...location.state };

    st.faculty_id = st.faculty_id ? String(st.faculty_id) : '';
    st.session_year = st.session_year ? String(st.session_year) : '';

    setBatchMasterData((prev) => ({
      ...prev,
      ...st,
      class_name: st.class_name || prev.class_name || '',
    }));

    const ts = location.state?.total_semester || 0;
    const cf = location.state?.course_structure || 'SEM';
    setTotSems(ts);
    setCourseFormat(cf);

    const incoming =
      location.state?.batchSubjectsList ||
      location.state?.Subjects ||
      location.state?.subjects ||
      location.state?.batch_subjects_list ||
      [];

    if (Array.isArray(incoming) && incoming.length > 0) {
      const sems = {};
      incoming.forEach((s) => {
        const sem = Number(s.semester) || 1;
        if (!sems[sem]) sems[sem] = [];
        sems[sem].push({
          tempId: makeTempId(),
          id: s.id ?? 0,
          class_subject_trx_id:
            s.class_subject_trx_id ?? s.classSubjectTrxId ?? 0,
          subject_id: s.subject_id ?? '',
          subject_name: s.subject_name ?? '',
          subject_code: s.subject_code ?? '',
          faculty_id: s.faculty_id ? String(s.faculty_id) : '',
          faculty_name: s.faculty_name ?? '',
          semester: sem,
          isExisting: true,
        });
      });

      setSemesterSubjects(sems);
      didInitialAutoLoad.current = true;

      const batch_id = location.state?.id ?? 0;
      if (batch_id) loadFacultys(batch_id);
    } else {
      const vId = location.state?.vendor_id ?? 0;
      const bId = location.state?.branch_id ?? 0;
      const cId = location.state?.class_id ?? 0;
      const batch_id = location.state?.id ?? 0;

      if (cId && ts) {
        loadFacultys(batch_id);
        loadClassSubjects(vId, bId, cId, ts);
        didInitialAutoLoad.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const fetchFaculty = async (searchText) => {
    try {
      const res = await apiService.get(
        `api/FacultyMaster/GetAllFaculty?vendorId=${
          batchMasterData.vendor_id
        }&branchId=${batchMasterData.branch_id}&search=${encodeURIComponent(
          searchText || '',
        )}`,
      );

      if (res?.status === true) {
        const facultyRaw = res?.data || [];

        setFaculty(
          facultyRaw
            .map((x) => {
              const id = x.faculty_id ?? x.id ?? x.ID ?? 0;
              const name = x.faculty_name ?? x.name ?? x.Name ?? '';
              return { value: String(id), label: String(name) };
            })
            .filter((x) => x.value !== '0' && x.label),
        );
      } else {
        toast.error(res?.message || 'Failed to fetch');
      }
    } catch (err) {
      console.log(err);
      toast.error('Failed to load faculty');
    }
  };

  useEffect(() => {
    if (!faculty || faculty.length === 0) return;

    setSemesterSubjects((prev) => {
      let changed = false;
      const next = {};

      Object.keys(prev || {}).forEach((semKey) => {
        const rows = prev[semKey] || [];
        next[semKey] = rows.map((r) => {
          if (!r.faculty_id || r.faculty_name) return r;

          const f = faculty.find(
            (x) => String(x.value) === String(r.faculty_id),
          );
          if (!f) return r;

          changed = true;
          return { ...r, faculty_name: f.label };
        });
      });

      return changed ? next : prev;
    });
  }, [faculty]);

  const fetchClasses = async (searchText) => {
    try {
      const res = await apiService.get(
        `api/ClassMaster/GetClassMaster?vendorId=${
          batchMasterData.vendor_id
        }&branchId=${batchMasterData.branch_id}&searchItem=${encodeURIComponent(
          searchText || '',
        )}`,
      );

      if (res.status === true) {
        const classesRaw = res.data || [];
        setRawClasses(classesRaw);

        const classOptions = classesRaw.map((x) => ({
          value: x.id,
          label: x.class_Name ?? x.class_display_name ?? 'No Name',
        }));
        setClasses(classOptions);

        if (batchMasterData.class_id) {
          const cls = classesRaw.find(
            (c) => Number(c.id) === Number(batchMasterData.class_id),
          );
          if (cls) {
            setTotSems(cls?.total_semester || 0);
            setCourseFormat(cls?.course_structure || 'SEM');

            setBatchMasterData((prev) => ({
              ...prev,
              class_name:
                prev.class_name ||
                cls?.class_Name ||
                cls?.class_display_name ||
                '',
              course_structure: cls?.course_structure || 'SEM',
              duration: cls?.duration || '',
              total_semester: cls?.total_semester || 0,
            }));

            if (
              !didInitialAutoLoad.current &&
              Number(cls?.total_semester || 0) > 0
            ) {
              loadClassSubjects(
                batchMasterData.vendor_id,
                batchMasterData.branch_id,
                batchMasterData.class_id,
                cls?.total_semester || 0,
              );
              didInitialAutoLoad.current = true;
            }
          }
        }
      } else {
        toast.error(res.message || 'Failed to fetch');
      }
    } catch (err) {
      console.log(err);
      toast.error('Failed to load courses');
    }
  };

  useEffect(() => {
    const { vendor_id, branch_id, id } = batchMasterData;

    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: vendor_id,
      branchId: branch_id,
    });

    if (!canLoad) return;

    fetchClasses('');
    fetchFaculty('');
    loadSubjectLookup(vendor_id, branch_id);
    fetchSessions('');

    if (id) loadFacultys(id);

    if (
      batchMasterData.class_id &&
      Number(batchMasterData.total_semester) > 0
    ) {
      loadClassSubjects(
        vendor_id,
        branch_id,
        batchMasterData.class_id,
        batchMasterData.total_semester,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchMasterData.vendor_id, batchMasterData.branch_id]);

  const onCourseSelectChange = async (opt) => {
    facultyMapRef.current = { byCs: {}, bySemSub: {} };

    if (!opt) {
      setTotSems(0);
      setCourseFormat('SEM');
      setSemesterSubjects({});
      didInitialAutoLoad.current = false;

      setBatchMasterData((prev) => ({
        ...prev,
        class_id: 0,
        class_name: '',
        course_structure: '',
        duration: '',
        total_semester: 0,
      }));

      setBatchErrors((prev) => ({ ...prev, class_id: '' }));
      return;
    }

    const classId = opt.value;

    const classData = (rawClasses || []).find(
      (x) => Number(x.id) === Number(classId),
    );

    const totalSem = classData?.total_semester || 0;
    const format = classData?.course_structure || 'SEM';

    resetTouchedAutoFields();

    setTotSems(totalSem);
    setCourseFormat(format);

    setBatchMasterData((prev) => ({
      ...prev,
      class_id: classId,
      class_name: opt.label,
      course_structure: format,
      duration: classData?.duration || '',
      total_semester: totalSem || 0,
    }));

    setBatchErrors((prev) => ({ ...prev, class_id: '' }));

    await autoFillBatchFieldsByCourse(classData, opt, true);

    loadClassSubjects(
      batchMasterData.vendor_id,
      batchMasterData.branch_id,
      classId,
      totalSem,
    );

    didInitialAutoLoad.current = true;

    if (batchMasterData.id) loadFacultys(batchMasterData.id);
  };

  const handleSemesterFacultyChange = (semester, index, faculty_id) => {
    setSemesterSubjects((prev) => {
      const clone = [...(prev[semester] || [])];
      const row = { ...clone[index] };

      const fid = faculty_id ? String(faculty_id) : '';
      row.faculty_id = fid;

      const f = faculty.find((x) => String(x.value) === fid);
      row.faculty_name = f?.label || '';

      clone[index] = row;
      return { ...prev, [semester]: clone };
    });
  };

  const closeTimeTableModal = () => {
    setShowTimeTableModal(false);
    setTimeTableErrors({});
    setTimeTableContext({ semester: 0, index: -1 });
    setTimeTableData(buildDefaultTimeTableState());
    setSubjectTimeTableRows([]);
    setSubjectTimeTableLoading(false);
    setTimeTableDeletingId(0);
  };

  const openTimeTableModal = (row, semester, index) => {
    if (!batchMasterData.id || Number(batchMasterData.id) <= 0) {
      toast.error('Please save batch first, then add time table.');
      return;
    }

    if (!batchMasterData.class_id || Number(batchMasterData.class_id) <= 0) {
      toast.error('Please select course first.');
      return;
    }

    if (!row?.subject_id || Number(row.subject_id) <= 0) {
      toast.error('Subject is not available for this row.');
      return;
    }

    const selectedFacultyId = String(
      row?.faculty_id || batchMasterData.faculty_id || '',
    ).trim();

    if (!selectedFacultyId || Number(selectedFacultyId) <= 0) {
      toast.error('Please select faculty first, then add time table.');
      return;
    }

    const selectedFaculty = faculty.find(
      (x) => String(x.value) === selectedFacultyId,
    );
    const selectedFacultyName =
      row?.faculty_name || selectedFaculty?.label || '';

    const nextTimeTableData = {
      id: 0,
      class_id: Number(batchMasterData.class_id || 0),
      batch_id: Number(batchMasterData.id || 0),
      day_id: '',
      start_time: '',
      end_time: '',
      subject_id: Number(row.subject_id || 0),
      faculty_id: selectedFacultyId,
      vendor_id: Number(batchMasterData.vendor_id || 0),
      branch_id: Number(batchMasterData.branch_id || 0),
      class_name: batchMasterData.class_name || '',
      batch_name: batchMasterData.batch_name || '',
      subject_name: row.subject_name || '',
      subject_code: row.subject_code || '',
      faculty_name: selectedFacultyName,
      day_name: '',
    };

    setTimeTableContext({ semester, index });
    setTimeTableErrors({});
    setTimeTableData(nextTimeTableData);
    setShowTimeTableModal(true);

    loadSubjectTimeTables({
      classId: nextTimeTableData.class_id,
      batchId: nextTimeTableData.batch_id,
      subjectId: nextTimeTableData.subject_id,
      vendorId: nextTimeTableData.vendor_id,
      branchId: nextTimeTableData.branch_id,
    });
  };

  const handleEditTimeTableRow = (item) => {
    const editDayId = Number(item?.day_id ?? item?.dayId ?? 0);

    const editFacultyId = String(
      item?.faculty_id ?? item?.facultyId ?? timeTableData.faculty_id ?? '',
    ).trim();

    const selectedFaculty = faculty.find(
      (x) => String(x.value) === editFacultyId,
    );

    setTimeTableErrors({});

    setTimeTableData((prev) => ({
      ...prev,
      id: Number(item?.id ?? item?.time_table_id ?? item?.timeTableId ?? 0),
      day_id: editDayId,
      start_time: normalizeTimeSelectorValue(
        item?.start_time ?? item?.startTime ?? '',
      ),
      end_time: normalizeTimeSelectorValue(
        item?.end_time ?? item?.endTime ?? '',
      ),
      faculty_id: editFacultyId || String(prev.faculty_id || ''),
      faculty_name:
        item?.faculty_name ??
        item?.facultyName ??
        selectedFaculty?.label ??
        prev.faculty_name ??
        '',
      day_name: item?.day_name ?? item?.dayName ?? getDayLabel(editDayId),
    }));
  };

  const deleteTimeTableRow = async (item) => {
    try {
      const rowId = Number(
        item?.id ?? item?.time_table_id ?? item?.timeTableId ?? 0,
      );

      if (!rowId) {
        toast.error('Invalid time table row');
        return;
      }

      const confirmed = window.confirm(
        'Are you sure you want to delete this time table row?',
      );
      if (!confirmed) return;

      setTimeTableDeletingId(rowId);

      const response = await apiService.delete(
        `api/TimeTableMaster/DeleteTimeTable?id=${rowId}`,
      );

      const responseEnvelope = getApiEnvelope(response);

      const isSuccess = responseEnvelope?.status === true;
      const responseMessage =
        responseEnvelope?.message ||
        responseEnvelope?.Message ||
        (isSuccess
          ? 'Time table deleted successfully'
          : 'Failed to delete time table');

      if (isSuccess) {
        toast.success(responseMessage);

        if (Number(timeTableData.id || 0) === rowId) {
          setTimeTableData((prev) => ({
            ...prev,
            id: 0,
            day_id: '',
            start_time: '',
            end_time: '',
            day_name: '',
          }));
        }

        await loadSubjectTimeTables({
          classId: Number(timeTableData.class_id || 0),
          batchId: Number(timeTableData.batch_id || 0),
          subjectId: Number(timeTableData.subject_id || 0),
          vendorId: Number(timeTableData.vendor_id || 0),
          branchId: Number(timeTableData.branch_id || 0),
        });
      } else {
        toast.error(responseMessage);
      }
    } catch (err) {
      console.error(err);
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.response?.data?.title ||
        err?.message ||
        'Something went wrong while deleting time table';
      toast.error(apiMessage);
    } finally {
      setTimeTableDeletingId(0);
    }
  };

  const onTimeTableInputChange = (e) => {
    const { name, value } = e.target;

    setTimeTableData((prev) => {
      const next = {
        ...prev,
        [name]:
          name === 'day_id' ||
          name === 'class_id' ||
          name === 'batch_id' ||
          name === 'subject_id' ||
          name === 'vendor_id' ||
          name === 'branch_id'
            ? Number(value || 0)
            : value,
      };

      if (name === 'faculty_id') {
        const selectedFaculty = faculty.find(
          (x) => String(x.value) === String(value || ''),
        );
        next.faculty_name = selectedFaculty?.label || '';
      }

      if (name === 'day_id') {
        next.day_name = getDayLabel(value);
      }

      return next;
    });

    setTimeTableErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
  };

  const validateTimeTable = () => {
    const errors = {};

    if (!timeTableData.class_id || Number(timeTableData.class_id) <= 0)
      errors.class_id = 'Class is required';

    if (!timeTableData.batch_id || Number(timeTableData.batch_id) <= 0)
      errors.batch_id = 'Batch is required';

    if (!timeTableData.day_id || Number(timeTableData.day_id) <= 0)
      errors.day_id = 'Day is required';

    if (!timeTableData.subject_id || Number(timeTableData.subject_id) <= 0)
      errors.subject_id = 'Subject is required';

    if (!timeTableData.faculty_id || Number(timeTableData.faculty_id) <= 0)
      errors.faculty_id = 'Faculty is required';

    const startApiTime = toApiTime(timeTableData.start_time);
    const endApiTime = toApiTime(timeTableData.end_time);

    if (!startApiTime) errors.start_time = 'Start time is required';
    if (!endApiTime) errors.end_time = 'End time is required';

    const startSeconds = timeToSeconds(timeTableData.start_time);
    const endSeconds = timeToSeconds(timeTableData.end_time);

    if (
      startSeconds != null &&
      endSeconds != null &&
      endSeconds <= startSeconds
    ) {
      errors.end_time = 'End time must be greater than start time';
    }

    return errors;
  };

  const syncTimeTableFacultyToRow = (facultyId, facultyName) => {
    if (!timeTableContext.semester || timeTableContext.index < 0) return;

    setSemesterSubjects((prev) => {
      const rows = [...(prev[timeTableContext.semester] || [])];
      if (!rows[timeTableContext.index]) return prev;

      rows[timeTableContext.index] = {
        ...rows[timeTableContext.index],
        faculty_id: String(facultyId || ''),
        faculty_name: facultyName || '',
      };

      return {
        ...prev,
        [timeTableContext.semester]: rows,
      };
    });
  };

  const saveTimeTableFromModal = async () => {
    try {
      const errors = validateTimeTable();

      if (Object.keys(errors).length > 0) {
        setTimeTableErrors(errors);

        if (errors.faculty_id) {
          toast.error('Please select faculty first, then save time table.');
        } else if (errors.class_id || errors.batch_id || errors.subject_id) {
          toast.error('Required time table data is missing.');
        }

        return;
      }

      setTimeTableLoading(true);

      const payload = {
        id: Number(timeTableData.id || 0),
        class_id: Number(timeTableData.class_id || 0),
        batch_id: Number(timeTableData.batch_id || 0),
        day_id: Number(timeTableData.day_id || 0),
        start_time: toApiTime(timeTableData.start_time),
        end_time: toApiTime(timeTableData.end_time),
        subject_id: Number(timeTableData.subject_id || 0),
        faculty_id: Number(timeTableData.faculty_id || 0),
        vendor_id: Number(timeTableData.vendor_id || 0),
        branch_id: Number(timeTableData.branch_id || 0),
        class_name: timeTableData.class_name || '',
        batch_name: timeTableData.batch_name || '',
        subject_name: timeTableData.subject_name || '',
        subject_code: timeTableData.subject_code || '',
        faculty_name: timeTableData.faculty_name || '',
        day_name: getDayLabel(timeTableData.day_id),
        student_count: 0,
        student_names_text: '',
      };

      const config = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };

      const isEditMode = Number(timeTableData.id || 0) > 0;

      const response = isEditMode
        ? await apiService.put(
            `api/TimeTableMaster/UpdateTimeTable?id=${payload.id}`,
            payload,
            config,
          )
        : await apiService.post(
            'api/TimeTableMaster/AddTimeTable',
            payload,
            config,
          );

      const responseEnvelope = getApiEnvelope(response);

      const isSuccess = responseEnvelope?.status === true;
      const responseMessage =
        responseEnvelope?.message ||
        responseEnvelope?.Message ||
        (isSuccess
          ? isEditMode
            ? 'Time table updated successfully'
            : 'Time table saved successfully'
          : isEditMode
            ? 'Failed to update time table'
            : 'Failed to save time table');

      if (isSuccess) {
        syncTimeTableFacultyToRow(payload.faculty_id, payload.faculty_name);
        toast.success(responseMessage);

        await loadSubjectTimeTables({
          classId: payload.class_id,
          batchId: payload.batch_id,
          subjectId: payload.subject_id,
          vendorId: payload.vendor_id,
          branchId: payload.branch_id,
        });

        setTimeTableData((prev) => ({
          ...prev,
          id: 0,
          day_id: '',
          start_time: '',
          end_time: '',
          day_name: '',
        }));
      } else {
        toast.error(responseMessage);
      }
    } catch (err) {
      console.error(err);
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        err?.response?.data?.title ||
        err?.message ||
        'Something went wrong while saving time table';
      toast.error(apiMessage);
    } finally {
      setTimeTableLoading(false);
    }
  };

  const validateInputs = () => {
    const errors = {};

    if (
      isSuperAdmin &&
      (!batchMasterData.vendor_id || batchMasterData.vendor_id <= 0)
    ) {
      toast.error('Please select a Vendor');
      return false;
    }

    if (
      (isSuperAdmin || isAdmin || hasMultiBranch) &&
      (!batchMasterData.branch_id || batchMasterData.branch_id <= 0)
    ) {
      toast.error('Please select a Branch');
      return false;
    }

    if (!batchMasterData.batch_name)
      errors.batch_name = 'Batch name is required';
    if (!batchMasterData.batch_code)
      errors.batch_code = 'Batch code is required';

    if (!batchMasterData.class_id || batchMasterData.class_id <= 0)
      errors.class_id = 'Course is required';

    if (!batchMasterData.class_name)
      errors.class_name = 'Class name is required';

    if (!batchMasterData.batch_start_time)
      errors.batch_start_time = 'Start time is required';
    if (!batchMasterData.batch_end_time)
      errors.batch_end_time = 'End time is required';
    if (!batchMasterData.batch_start_date)
      errors.batch_start_date = 'Start date is required';
    if (!batchMasterData.batch_end_date)
      errors.batch_end_date = 'End date is required';
    if (!batchMasterData.faculty_id) errors.faculty_id = 'Faculty is required';
    if (!batchMasterData.session_year)
      errors.session_year = 'Session Year is required';

    return errors;
  };

  const saveBatchMaster = async () => {
    try {
      const errors = validateInputs();
      if (errors === false) return;

      if (Object.keys(errors).length > 0) {
        setBatchErrors(errors);
        return;
      }

      setLoading(true);

      const headFidStr = batchMasterData.faculty_id
        ? String(batchMasterData.faculty_id)
        : '';

      const subjectsToSend = [];
      Object.keys(semesterSubjects || {}).forEach((sem) => {
        const semNum = Number(sem);
        const rows = semesterSubjects[sem] || [];

        rows.forEach((row) => {
          if (!row.subject_id) return;

          const csId = Number(row.class_subject_trx_id || 0);
          if (!csId) return;

          const fidStr = String(row.faculty_id || headFidStr || '').trim();
          const f = faculty.find((x) => String(x.value) === fidStr);

          subjectsToSend.push({
            id: row.id ?? 0,
            class_subject_trx_id: csId,
            subject_id: Number(row.subject_id || 0),
            subject_name: row.subject_name ?? '',
            subject_code: row.subject_code ?? '',
            semester: semNum,
            faculty_id: fidStr ? Number(fidStr) : 0,
            faculty_name: row.faculty_name || f?.label || '',
          });
        });
      });

      const payloadObj = { ...batchMasterData };
      delete payloadObj.Subjects;
      delete payloadObj.subjects;
      delete payloadObj.newSubjects;
      delete payloadObj.newsubjects;
      delete payloadObj.batchSubjectsList;

      payloadObj.batchSubjectsList = subjectsToSend;
      payloadObj.faculty_id = payloadObj.faculty_id
        ? Number(payloadObj.faculty_id)
        : 0;
      payloadObj.session_year = String(payloadObj.session_year || '').trim();
      payloadObj.class_name = String(payloadObj.class_name || '').trim();

      const config = {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };

      const data = JSON.stringify(payloadObj);

      let response;
      if (batchMasterData.id !== 0) {
        response = await apiService.put(
          `api/BatchMaster/UpdateBatch?id=${batchMasterData.id}`,
          data,
          config,
        );
      } else {
        response = await apiService.post(
          'api/BatchMaster/AddBatch',
          data,
          config,
        );
      }

      if (response.status === true) {
        toast(response.message);
        navigate('/list-batch');
      } else {
        toast(response.message);
      }
    } catch (err) {
      console.log(err);
      toast('Something went wrong!!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className='bg-white pd-5 card-box mb-5 toolbar-row sticky-top border text-right mb-2'>
        <Button
          color='secondary'
          size='sm'
          onClick={onHandleBack}
          className='mr-2'
        >
          <i className='bi bi-arrow-left' /> Back
        </Button>

        <Button color='primary' size='sm' onClick={saveBatchMaster}>
          <i className='bi bi-check-circle' /> {loading ? 'Saving...' : 'Save'}
        </Button>
      </div>

      <VendorBranchSelector
        user={user}
        selectionData={batchMasterData}
        setSelectionData={setBatchMasterData}
        errorMessage={batchErrors}
        accessButton={true}
      />

      <Card className='card-box height-100-p pd-20 mb-20'>
        <CardTitle
          tag='h6'
          className='border-bottom p-3 mb-0 d-flex align-items-center justify-content-between'
        >
          <div>
            Add new Batch for Course - {batchMasterData.class_name || ''}
          </div>
        </CardTitle>

        <CardBody>
          <Form className='row'>
            <FormGroup className='col-6'>
              <Label>Course</Label>
              <Select
                options={classes}
                value={classes.find(
                  (o) => Number(o.value) === Number(batchMasterData.class_id),
                )}
                onChange={onCourseSelectChange}
                onInputChange={(inputValue, actionMeta) => {
                  if (actionMeta.action === 'input-change')
                    fetchClasses(inputValue);
                }}
                isSearchable
                isClearable
                placeholder='Search courses ...'
              />
              {batchErrors.class_id && (
                <FormText color='danger'>{batchErrors.class_id}</FormText>
              )}
            </FormGroup>

            <FormGroup className='col-6'>
              <Label>Batch Name</Label>
              <Input
                name='batch_name'
                value={batchMasterData.batch_name}
                onChange={onBatchInputChange}
              />
              {batchErrors.batch_name && (
                <FormText color='danger'>{batchErrors.batch_name}</FormText>
              )}
            </FormGroup>

            <FormGroup className='col-6'>
              <Label>Batch Code</Label>
              <Input
                name='batch_code'
                value={batchMasterData.batch_code}
                onChange={onBatchInputChange}
              />
              {batchErrors.batch_code && (
                <FormText color='danger'>{batchErrors.batch_code}</FormText>
              )}
            </FormGroup>

            <FormGroup className='col-6'>
              <Label>Head Faculty</Label>
              <Select
                options={faculty}
                value={faculty.find(
                  (x) => String(x.value) === String(batchMasterData.faculty_id),
                )}
                onChange={(opt) => onSelectChange('faculty_id', opt)}
                onInputChange={(inputValue, actionMeta) => {
                  if (actionMeta.action === 'input-change')
                    fetchFaculty(inputValue);
                }}
                isSearchable
                isClearable
                placeholder='Select faculty'
              />
              {batchErrors.faculty_id && (
                <FormText color='danger'>{batchErrors.faculty_id}</FormText>
              )}
            </FormGroup>

            <TimeSelector
              label='Batch Start Time'
              value={batchMasterData.batch_start_time}
              col='col-6'
              onChange={(val) =>
                setBatchMasterData((prev) => ({
                  ...prev,
                  batch_start_time: val,
                }))
              }
              errorMsg={batchErrors.batch_start_time}
            />

            <TimeSelector
              label='Batch End Time'
              value={batchMasterData.batch_end_time}
              col='col-6'
              onChange={(val) =>
                setBatchMasterData((prev) => ({
                  ...prev,
                  batch_end_time: val,
                }))
              }
              errorMsg={batchErrors.batch_end_time}
            />

            <FormGroup className='col-6'>
              <Label>Batch Start Date</Label>
              <Input
                type='date'
                name='batch_start_date'
                value={formatDate(
                  batchMasterData.batch_start_date,
                  'YYYY-MM-DD',
                )}
                onChange={onBatchInputChange}
              />
              {batchErrors.batch_start_date && (
                <FormText color='danger'>
                  {batchErrors.batch_start_date}
                </FormText>
              )}
            </FormGroup>

            <FormGroup className='col-6'>
              <Label>Batch End Date</Label>
              <Input
                type='date'
                name='batch_end_date'
                value={formatDate(batchMasterData.batch_end_date, 'YYYY-MM-DD')}
                onChange={onBatchInputChange}
              />
              {batchErrors.batch_end_date && (
                <FormText color='danger'>{batchErrors.batch_end_date}</FormText>
              )}
            </FormGroup>

            <FormGroup className='col-sm-4'>
              <Label>Session Year</Label>
              <Select
                options={sessionOptions}
                value={sessionOptions.find(
                  (o) =>
                    String(o.label).trim() ===
                    String(batchMasterData.session_year || '').trim(),
                )}
                onChange={(opt) =>
                  setBatchMasterData((prev) => ({
                    ...prev,
                    session_year: opt ? String(opt.label).trim() : '',
                  }))
                }
                onInputChange={(inputValue, actionMeta) => {
                  if (actionMeta.action === 'input-change')
                    fetchSessions(inputValue);
                }}
                isSearchable
                isClearable
                placeholder='Select session (e.g. 2026-2027)'
              />
              {batchErrors.session_year && (
                <FormText color='danger'>{batchErrors.session_year}</FormText>
              )}
            </FormGroup>
          </Form>
        </CardBody>
      </Card>

      {totSems > 0 && courseFormat && (
        <div className='faq-wrap'>
          <h4 className='mb-30 h4 text-blue padding-top-30'>Batch Subjects</h4>

          <div className='padding-bottom-30'>
            {Array.from({ length: totSems || 0 }, (_, i) => {
              const semNum = i + 1;
              const subjects = semesterSubjects[semNum] || [];

              return (
                <div className='card mb-3' key={semNum}>
                  <div className='card-header' id={`headingBatchSem${semNum}`}>
                    <button
                      className='btn btn-block w-100 d-flex justify-content-between align-items-center'
                      type='button'
                      data-toggle='collapse'
                      data-target={`#collapseBatchSem${semNum}`}
                      aria-controls={`collapseBatchSem${semNum}`}
                      style={{ fontWeight: 600 }}
                    >
                      <span>
                        {(courseFormat || 'SEM').toUpperCase()} - {semNum}
                      </span>
                      <small className='text-muted'>(Click to show/hide)</small>
                    </button>
                  </div>

                  <div
                    id={`collapseBatchSem${semNum}`}
                    className='collapse'
                    aria-labelledby={`headingBatchSem${semNum}`}
                  >
                    <div className='card-body'>
                      <table className='table table-bordered table-hover'>
                        <thead className='bg-primary text-white'>
                          <tr>
                            <th style={{ width: 80 }}>S. no.</th>
                            <th>Subject Name</th>
                            <th style={{ width: 200 }}>Subject Code</th>
                            <th style={{ width: 420 }}>
                              Faculty Name / Time Table
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {subjects.length > 0 ? (
                            subjects.map((row, index) => (
                              <tr key={row.tempId || index}>
                                <td>{index + 1}</td>

                                <td>
                                  <input
                                    type='text'
                                    className='form-control'
                                    value={row.subject_name || ''}
                                    readOnly
                                  />
                                </td>

                                <td>
                                  <input
                                    type='text'
                                    className='form-control'
                                    value={row.subject_code || ''}
                                    readOnly
                                  />
                                </td>

                                <td>
                                  <div
                                    className='d-flex align-items-center'
                                    style={{ gap: '8px' }}
                                  >
                                    <select
                                      className='form-control'
                                      value={String(row.faculty_id || '')}
                                      onChange={(e) =>
                                        handleSemesterFacultyChange(
                                          semNum,
                                          index,
                                          e.target.value,
                                        )
                                      }
                                    >
                                      <option value=''>
                                        -- Select Faculty --
                                      </option>
                                      {faculty.map((f, k) => (
                                        <option key={k} value={String(f.value)}>
                                          {f.label}
                                        </option>
                                      ))}
                                    </select>

                                    <Button
                                      color='primary'
                                      size='sm'
                                      type='button'
                                      style={{ whiteSpace: 'nowrap' }}
                                      onClick={() =>
                                        openTimeTableModal(row, semNum, index)
                                      }
                                    >
                                      Time Table
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} className='text-center'>
                                No subjects found for this{' '}
                                {(courseFormat || 'SEM').toUpperCase()} -{' '}
                                {semNum}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Modal
        isOpen={showTimeTableModal}
        toggle={closeTimeTableModal}
        size='lg'
        centered
      >
        <ModalHeader toggle={closeTimeTableModal}>
          {Number(timeTableData.id || 0) > 0
            ? 'Edit Time Table'
            : 'Add Time Table'}
        </ModalHeader>

        <ModalBody>
          <div className='row'>
            <FormGroup className='col-md-12'>
              <Label>Day</Label>
              <Input
                type='select'
                name='day_id'
                value={timeTableData.day_id || ''}
                onChange={onTimeTableInputChange}
              >
                <option value=''>-- Select Day --</option>
                {dayOptions.map((day) => (
                  <option key={day.value} value={day.value}>
                    {day.label}
                  </option>
                ))}
              </Input>
              {timeTableErrors.day_id && (
                <FormText color='danger'>{timeTableErrors.day_id}</FormText>
              )}
            </FormGroup>

            <TimeSelector
              label='Start Time'
              value={timeTableData.start_time}
              col='col-md-6'
              onChange={(val) => {
                setTimeTableData((prev) => ({
                  ...prev,
                  start_time: val,
                }));
                setTimeTableErrors((prev) => ({
                  ...prev,
                  start_time: '',
                }));
              }}
              errorMsg={timeTableErrors.start_time}
            />

            <TimeSelector
              label='End Time'
              value={timeTableData.end_time}
              col='col-md-6'
              onChange={(val) => {
                setTimeTableData((prev) => ({
                  ...prev,
                  end_time: val,
                }));
                setTimeTableErrors((prev) => ({
                  ...prev,
                  end_time: '',
                }));
              }}
              errorMsg={timeTableErrors.end_time}
            />

            <div className='col-12 mt-2'>
              <div className='border rounded p-3'>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <div className='font-weight-bold'>Time Table List</div>
                  <small className='text-muted'>
                    Total: {subjectTimeTableRows.length}
                  </small>
                </div>

                {subjectTimeTableLoading ? (
                  <div className='text-center py-3'>Loading...</div>
                ) : subjectTimeTableRows.length === 0 ? (
                  <div className='text-center text-muted py-3'>
                    No time table found for this subject.
                  </div>
                ) : (
                  <div
                    className='table-responsive'
                    style={{ maxHeight: '230px', overflowY: 'auto' }}
                  >
                    <table className='table table-bordered table-sm mb-0'>
                      <thead>
                        <tr>
                          <th style={{ width: '60px' }}>#</th>
                          <th>Day</th>
                          <th>Start Time</th>
                          <th>End Time</th>
                          <th style={{ width: '170px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subjectTimeTableRows.map((item, idx) => {
                          const rowId = Number(
                            item?.id ??
                              item?.time_table_id ??
                              item?.timeTableId ??
                              0,
                          );
                          const isDeleting = timeTableDeletingId === rowId;

                          return (
                            <tr
                              key={
                                item.id ||
                                `${item.subject_id}_${item.day_id}_${idx}`
                              }
                            >
                              <td>{idx + 1}</td>
                              <td>
                                {item.day_name || getDayLabel(item.day_id)}
                              </td>
                              <td>
                                {formatTimeForDisplay(
                                  item.start_time || item.startTime,
                                )}
                              </td>
                              <td>
                                {formatTimeForDisplay(
                                  item.end_time || item.endTime,
                                )}
                              </td>
                              <td>
                                <div
                                  className='d-flex align-items-center'
                                  style={{ gap: '6px' }}
                                >
                                  <Button
                                    color='warning'
                                    size='sm'
                                    type='button'
                                    disabled={timeTableLoading || isDeleting}
                                    onClick={() => handleEditTimeTableRow(item)}
                                  >
                                    Edit
                                  </Button>

                                  <Button
                                    color='danger'
                                    size='sm'
                                    type='button'
                                    disabled={timeTableLoading || isDeleting}
                                    onClick={() => deleteTimeTableRow(item)}
                                  >
                                    {isDeleting ? 'Deleting...' : 'Delete'}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            color='secondary'
            onClick={closeTimeTableModal}
            disabled={timeTableLoading || Boolean(timeTableDeletingId)}
          >
            Close
          </Button>

          <Button
            color='primary'
            onClick={saveTimeTableFromModal}
            disabled={timeTableLoading || Boolean(timeTableDeletingId)}
          >
            {timeTableLoading
              ? Number(timeTableData.id || 0) > 0
                ? 'Updating...'
                : 'Saving...'
              : Number(timeTableData.id || 0) > 0
                ? 'Update Time Table'
                : 'Save Time Table'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default AddEditBatch;
